import { useState, useEffect, useCallback, useMemo } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Slide,
  Fade,
  useTheme,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Check, CheckCircleOutline, MailOutline } from '@mui/icons-material';
import { useGetSurveysQuery, useUpdateSurveyMutation } from '../../services/projects/projectsApi';

const FeedbackPopup = () => {
  const { t: trans } = useLingui();
  const [showQuestion1, setShowQuestion1] = useState(false);
  const [showThankYou1, setShowThankYou1] = useState(false);
  const [showQuestion2, setShowQuestion2] = useState(false);
  const [showThankYou2, setShowThankYou2] = useState(false);
  const [showBottomMessage, setShowBottomMessage] = useState(false);
  const [question2Answer, setQuestion2Answer] = useState('');
  const [rating, setRating] = useState(0);
  const [contactMe, setContactMe] = useState(false);

  const { data: surveys, isLoading } = useGetSurveysQuery();

  const [sendAnswer] = useUpdateSurveyMutation();

  const hasVisibleContent =
    showQuestion1 || showThankYou1 || showQuestion2 || showThankYou2 || showBottomMessage;

  const messages = useMemo(() => {
    if (!surveys?.length) return { question1: '', question2: '' };
    return {
      question1: surveys[0].title,
      question2: surveys[0].comments_label,
    };
  }, [surveys]);

  const { palette } = useTheme();

  useEffect(() => {
    if (!surveys?.length || isLoading) return;
    const timer = setTimeout(() => {
      setShowQuestion1(true);
    }, 60000);

    return () => clearTimeout(timer);
  }, [isLoading, surveys?.length]);

  const handleQuestion1Submit = useCallback((answer: number) => {
    setRating(answer);
    setShowQuestion1(false);
    setShowThankYou1(true);
    setShowQuestion2(true);

    setTimeout(() => {
      setShowThankYou1(false);
    }, 5000);
  }, []);

  const handleQuestion2Submit = () => {
    saveAnswer();
    setShowQuestion2(false);
    setShowThankYou2(true);
    setShowBottomMessage(true);

    setTimeout(() => {
      setShowThankYou2(false);
    }, 5000);
  };

  const handleCloseQuestion1 = () => {
    saveAnswer();
    setShowQuestion1(false);
    setShowBottomMessage(true);
  };

  const handleCloseQuestion2 = () => {
    saveAnswer();
    setShowQuestion2(false);
    setShowBottomMessage(true);
  };

  const saveAnswer = () => {
    if (!surveys?.[0].id) return;
    sendAnswer({
      id: surveys?.[0].id,
      rating: rating,
      comments: question2Answer,
      contact_me: contactMe,
    });
  };

  const cardStyles = {
    bgcolor: palette.background.paperElevation2,
    boxShadow: palette.shadows[28],
    overflow: 'hidden',
    color: palette.text.primary,
    p: '16px',
  };

  const ThankYouMessage = useCallback(
    ({ show }: { show: boolean }) => (
      <Fade in={show}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleOutline sx={{ color: palette.primary.main, width: 24, height: 24 }} />
            <Typography variant="body1">
              <Trans>Thank you!</Trans>
            </Typography>
          </Box>
        </Box>
      </Fade>
    ),
    [palette.primary.main]
  );

  if (!hasVisibleContent) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 32,
        right: 32,
        width: 406,
        maxWidth: '90vw',
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {/* Question 1 / Thank You 1 */}
      <Slide direction="left" in={showQuestion1 || showThankYou1} mountOnEnter unmountOnExit>
        <Box sx={cardStyles}>
          {showQuestion1 && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  pb: '8px',
                }}
              >
                <Typography variant="body1">{messages.question1}</Typography>
                <IconButton
                  size="small"
                  onClick={handleCloseQuestion1}
                  sx={{
                    color: palette.text.secondary,
                    ml: 2,
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              <Box>
                {/* Rating Buttons */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, 1fr)',
                    mb: '8px',
                  }}
                >
                  {[1, 2, 3, 4, 5].map((value) => (
                    <Button
                      key={value}
                      onClick={() => handleQuestion1Submit(value)}
                      variant="outlined"
                      color="midText"
                    >
                      {value}
                    </Button>
                  ))}
                </Box>

                {/* Labels */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    color: palette.text.secondary,
                  }}
                >
                  <Typography variant="caption2">
                    <Trans>Dissatisfied</Trans>
                  </Typography>
                  <Typography variant="caption2">
                    <Trans>Very Satisfied</Trans>
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {showThankYou1 && <ThankYouMessage show={showThankYou1} />}
        </Box>
      </Slide>

      {/* Question 2 / Thank You 2 */}
      <Slide direction="left" in={showQuestion2 || showThankYou2} mountOnEnter unmountOnExit>
        <Box sx={cardStyles}>
          {showQuestion2 && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 1,
                }}
              >
                <Typography variant="body1">{messages.question2}</Typography>
                <IconButton
                  size="small"
                  onClick={handleCloseQuestion2}
                  sx={{
                    color: palette.text.secondary,
                    mt: -0.5,
                    mr: -0.5,
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              <TextField
                fullWidth
                multiline
                rows={4}
                value={question2Answer}
                onChange={(e) => setQuestion2Answer(e.target.value)}
                variant="outlined"
              />
              <FormGroup>
                <FormControlLabel
                  sx={{ ml: 0 }}
                  control={<Checkbox onChange={(_, checked) => setContactMe(checked)} />}
                  label={trans`You can contact me for more feedback`}
                />
              </FormGroup>

              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleQuestion2Submit}
                disabled={!question2Answer.trim() && !contactMe}
                startIcon={<Check />}
                sx={{
                  mt: 1,
                  '&[disabled]': {
                    bgcolor: palette.background.paperElevation2,
                    boxShadow: palette.shadows[28],
                  },
                }}
              >
                <Trans>Submit</Trans>
              </Button>
            </Box>
          )}

          {showThankYou2 && <ThankYouMessage show={showThankYou2} />}
        </Box>
      </Slide>

      {/* Bottom Persistent Message */}
      <Slide direction="up" in={showBottomMessage} mountOnEnter>
        <Box sx={cardStyles}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <MailOutline sx={{ color: palette.text.secondary }} />
            <Typography variant="body1">
              <Trans>
                Submit feedback at any time from the Customer Support button in your user settings.
              </Trans>
            </Typography>
            <IconButton
              size="small"
              onClick={() => setShowBottomMessage(false)}
              sx={{
                color: palette.text.secondary,
                height: 24,
                width: 24,
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Slide>
    </Box>
  );
};

export default FeedbackPopup;
